import type {
  InventoryMovementHistory,
  RegisterInventoryMovementInput,
} from "gsintegrations";
import { axiosClient } from "../api/axiosClient";

export interface InventoryItemPayload {
  terreiroId?: string;
  mediumId?: string;
  category: string;
  description: string;
  imageUrl?: string;
  location: string;
  minQuantity: number;
  name: string;
  quantity: number;
  subcategory?: string;
  unit: string;
  consumable?: boolean;
  file?: File | Record<string, unknown>;
  estimatedUnitCost?: number;
}

export const inventoryItemRepository = {
  async createInventoryItem(payload: InventoryItemPayload) {
    // If file is present, use FormData for file upload
    if (payload.file instanceof File) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "file" && value instanceof File) {
          formData.append("file", value);
        } else if (value !== undefined) {
          formData.append(key, value as string);
        }
      });
      const response = await axiosClient.post("/inventory-item", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } else {
      // No file or file is not a File instance
      const response = await axiosClient.post("/inventory-item", payload, {
        withCredentials: true,
      });
      return response.data;
    }
  },
  async updateInventoryItem(
    inventoryItemId: string,
    payload: InventoryItemPayload
  ) {
    // If file is present, use FormData for file upload
    if (payload.file instanceof File) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "file" && value instanceof File) {
          formData.append("file", value);
        } else if (value !== undefined) {
          formData.append(key, value as string);
        }
      });
      const response = await axiosClient.put(
        `/inventory-item/${inventoryItemId}`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } else {
      // No file or file is not a File instance
      const response = await axiosClient.put(
        `/inventory-item/${inventoryItemId}`,
        payload,
        {
          withCredentials: true,
        }
      );
      return response.data;
    }
  },
  async getInventoryItemById(inventoryItemId: string) {
    const response = await axiosClient.get(
      `/inventory-item/${inventoryItemId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  },
  async deleteInventoryItem(inventoryItemId: string) {
    const response = await axiosClient.delete(
      `/inventory-item/${inventoryItemId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  },
  async listPendingReceipts(scope: { terreiroId?: string; mediumId?: string }) {
    const params = scope.terreiroId
      ? { terreiroId: scope.terreiroId }
      : { mediumId: scope.mediumId };
    const response = await axiosClient.get("/inventory-item/pending-receipts", {
      withCredentials: true,
      params,
    });
    return response.data as Array<{
      id: string;
      itemId: string;
      name: string;
      imageUrl?: string | null;
      quantity: number;
    }>;
  },
  async receiveStock(inventoryItemId: string, outcomeId: string) {
    const response = await axiosClient.post(
      `/inventory-item/${inventoryItemId}/receive-stock`,
      { outcomeId },
      { withCredentials: true },
    );
    return response.data as { applied: boolean };
  },
  async listMovements(
    inventoryItemId: string,
    params?: { page?: number; pageSize?: number },
  ) {
    const response = await axiosClient.get(
      `/inventory-item/${inventoryItemId}/movements`,
      {
        withCredentials: true,
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 20,
        },
      },
    );
    return response.data as InventoryMovementHistory;
  },
  async registerMovementEntry(
    inventoryItemId: string,
    input: RegisterInventoryMovementInput,
  ) {
    const response = await axiosClient.post(
      `/inventory-item/${inventoryItemId}/movements/entry`,
      input,
      { withCredentials: true },
    );
    return response.data;
  },
  async registerMovementExit(
    inventoryItemId: string,
    input: RegisterInventoryMovementInput,
  ) {
    const response = await axiosClient.post(
      `/inventory-item/${inventoryItemId}/movements/exit`,
      input,
      { withCredentials: true },
    );
    return response.data;
  },
};
